import { Button, Icons, Popover, Spin, Typography } from '@pankod/refine-antd';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { useState } from 'react';
import { getVariantStockByWarehouse } from 'services/inventory';
import { ShowroomStockEntry, orderShowroomStock } from 'utilities/showroom-stock';

interface ShowroomStockProps {
  productVariantId: string;
  currentWarehouseId: string;
}

// Stitches S5, flag inventory.allShowroomColumns: a cashier can see a variant's stock in every
// showroom, e.g. to tell a customer it is in stock elsewhere. Fetches one variant, when opened.
export const ShowroomStock: React.FC<ShowroomStockProps> = ({
  productVariantId,
  currentWarehouseId,
}) => {
  const isEnabled = useFeatureFlag('inventory.allShowroomColumns');
  const [entries, setEntries] = useState<ShowroomStockEntry[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isEnabled) return null;

  const loadStock = async () => {
    setEntries(null);
    setErrorMessage(null);
    try {
      const [variantStock] = await getVariantStockByWarehouse([productVariantId]);
      setEntries(orderShowroomStock(variantStock?.warehouseQuantities ?? [], currentWarehouseId));
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Could not load showroom stock');
    }
  };

  const content = errorMessage ? (
    <Typography.Text type="danger">{errorMessage}</Typography.Text>
  ) : entries === null ? (
    <Spin size="small" />
  ) : (
    <div style={{ minWidth: 200 }}>
      {entries.map((entry) => (
        <div
          key={entry.warehouseId ?? 'no-warehouse'}
          style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}
        >
          <Typography.Text
            strong={entry.isCurrent}
            type={entry.quantity > 0 ? undefined : 'secondary'}
          >
            {entry.isCurrent ? `${entry.warehouseName} (this POS)` : entry.warehouseName}
          </Typography.Text>
          <Typography.Text
            strong={entry.isCurrent}
            type={entry.quantity > 0 ? undefined : 'secondary'}
          >
            {entry.quantity}
          </Typography.Text>
        </div>
      ))}
    </div>
  );

  return (
    // Inside a POS search option: keep the click from adding the product or closing the list.
    <span
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {/* Above the POS search dropdown (layer 1050), which otherwise hides it. */}
      <Popover
        trigger="click"
        zIndex={1060}
        title="Stock in every showroom"
        content={content}
        onVisibleChange={(isVisible) => {
          if (isVisible) loadStock();
        }}
      >
        <Button type="link" size="small" icon={<Icons.ShopOutlined />} style={{ padding: 0 }}>
          Other showrooms
        </Button>
      </Popover>
    </span>
  );
};
